import React, { createContext, ReactNode, useContext, useMemo } from 'react';
import {
  collection,
  CollectionReference,
  getFirestore,
  limit,
  orderBy,
  query,
  Query,
} from 'firebase/firestore';
import { User } from 'firebase/auth';
import { TestExecution } from '@finvari/deliver';
import { useSubscribedList } from '../utils/firebaseHooks';

// How many recent executions to load. The workflow diagram and lane status only need the latest
// run per suite per PR, so a bounded recent window is plenty; the panel's full per-PR history uses
// a separate per-PR subscription.
const RECENT_TEST_EXECUTIONS_LIMIT = 300;

interface TestExecutionsContextType {
  executions: TestExecution[];
  // PR number -> that PR's executions, newest first.
  executionsByPr: Map<number, TestExecution[]>;
  loading: boolean;
  error: string | null;
}

const TestExecutionsContext = createContext<TestExecutionsContextType | undefined>(undefined);

export const useTestExecutions = () => {
  const context = useContext(TestExecutionsContext);
  if (context === undefined) {
    throw new Error('useTestExecutions must be used within a TestExecutionsProvider');
  }
  return context;
};

interface TestExecutionsProviderProps {
  children: ReactNode;
  user: User | null;
}

function recentExecutionsQuery(n: number): Query<TestExecution> {
  const db = getFirestore();
  const ref = collection(db, 'testExecutions') as CollectionReference<TestExecution>;
  return query(ref, orderBy('createdAt', 'desc'), limit(n));
}

export const TestExecutionsProvider: React.FC<TestExecutionsProviderProps> = ({ children, user }) => {
  // Gate on auth like the other providers: a signed-out subscribe gets a permission denial that
  // permanently kills the listener.
  const executionsQuery = useMemo(
    () => (user ? recentExecutionsQuery(RECENT_TEST_EXECUTIONS_LIMIT) : undefined),
    [user]
  );

  const { executions, loading, error } = useSubscribedList<TestExecution, 'executions'>(
    executionsQuery,
    'executions'
  );

  const executionsByPr = useMemo(() => {
    const map = new Map<number, TestExecution[]>();
    for (const exec of executions) {
      if (exec.prNumber === null || exec.prNumber === undefined) continue;
      const list = map.get(exec.prNumber);
      if (list) {
        list.push(exec);
      } else {
        map.set(exec.prNumber, [exec]);
      }
    }
    // The query already ordered by createdAt desc, so per-PR lists are newest-first.
    return map;
  }, [executions]);

  const value: TestExecutionsContextType = { executions, executionsByPr, loading, error };

  return (
    <TestExecutionsContext.Provider value={value}>
      {children}
    </TestExecutionsContext.Provider>
  );
};
